import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/Button';
import styles from './ConsentDialog.module.css';

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface ConsentDialogProps {
  /** Pre-formatted approximate cost, e.g. "~$0.001234". */
  costLabel: string;
  model: string;
  busy?: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * The app's first modal: a WCAG-conformant cost-consent dialog. `role="dialog"` +
 * `aria-modal` (not alertdialog — a considered, non-emergency decision); initial
 * focus on Cancel; a full Tab/Shift+Tab focus trap; Esc runs the same **decline**
 * (no API calls); focus returns to the trigger and background scroll is locked while
 * open. All styling via existing tokens (+ the new `--color-scrim`).
 */
export function ConsentDialog({
  costLabel,
  model,
  busy = false,
  onAccept,
  onDecline,
}: ConsentDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const declineRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Make the whole app background inert (unfocusable, non-interactive) — the
    // dialog is portaled to <body>, a sibling of #root, so it stays interactive.
    const appRoot = document.getElementById('root');
    appRoot?.setAttribute('inert', '');
    declineRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Document-level listener so the trap works wherever focus currently is.
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onDecline();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
    // Recover focus if it ever escapes the dialog by any route.
    function onFocusIn(event: FocusEvent) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        declineRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn);
      appRoot?.removeAttribute('inert');
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [onDecline]);

  return createPortal(
    <div className={styles.overlay}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="capability-consent-title"
        aria-describedby="capability-consent-desc"
        className={styles.dialog}
      >
        <h2 id="capability-consent-title" className={styles.title}>
          Run a capability check?
        </h2>
        <p id="capability-consent-desc" className={styles.desc}>
          This runs a one-time evaluation against your configured model ({model}) to check it
          against the AI journey-modeling capability floor. Estimated cost:{' '}
          <strong>{costLabel}</strong> — this is an approximate estimate; your provider’s actual
          charge may vary slightly.
        </p>
        <div className={styles.actions}>
          <Button ref={declineRef} type="button" variant="secondary" onClick={onDecline} data-testid="consent-decline">
            Cancel
          </Button>
          <Button type="button" variant="primary" busy={busy} onClick={onAccept} data-testid="consent-accept">
            Run check
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
